import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography, Stack, Stepper, Step, StepLabel,
  CircularProgress, Alert, Grid, MenuItem, Card, CardContent, Divider, List, ListItemButton, ListItemText, LinearProgress, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import MapIcon from '@mui/icons-material/Map';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { GoogleMap, Marker, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { baseURL } from '../../../config';
import { searchMapDrafts, upsertMapDraft, getMapDraft, uploadFarmImage, deleteFarmImage } from '../../../apis/drip-irrigation/api';

const randomId = () => {
  if (typeof window !== 'undefined' && window?.crypto?.randomUUID) return window.crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const safeNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

const debounce = (fn, waitMs) => {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), waitMs);
  };
};

const toLatLngLiteral = (value) => {
  if (!value) return null;
  if (Array.isArray(value) && value.length >= 2) {
    const a = Number(value[0]); const b = Number(value[1]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    const looksLikeLatLng = Math.abs(a) <= 90 && Math.abs(b) <= 180;
    const looksLikeLngLat = Math.abs(a) <= 180 && Math.abs(b) <= 90;
    if (looksLikeLatLng && !looksLikeLngLat) return { lat: a, lng: b };
    if (looksLikeLngLat && !looksLikeLatLng) return { lat: b, lng: a };
    return { lat: a, lng: b };
  }
  const lat = Number(value?.lat ?? value?.latitude);
  const lng = Number(value?.lng ?? value?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const normalizeBoundaryPoints = (points) => {
  const raw = Array.isArray(points) ? points : [];
  const normalized = raw.map((p) => toLatLngLiteral(p)).filter(Boolean).map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
  const deduped = [];
  for (const p of normalized) {
    const last = deduped[deduped.length - 1];
    if (last && last.lat === p.lat && last.lng === p.lng) continue;
    deduped.push(p);
  }
  const seen = new Set(); const unique = [];
  for (const p of deduped) {
    const key = `${p.lat},${p.lng}`;
    if (seen.has(key)) continue;
    seen.add(key); unique.push(p);
  }
  return unique;
};

const formatLatLngCompact = (lat, lng, digits = 5) => {
  const latNum = safeNumber(lat); const lngNum = safeNumber(lng);
  if (latNum == null || lngNum == null) return '';
  return `${latNum.toFixed(digits)}, ${lngNum.toFixed(digits)}`;
};

const makeSvgMarkerUrl = (svg) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

const getDraftImageUrls = (draft) => {
  const top = Array.isArray(draft?.imageUrls) ? draft.imageUrls : [];
  const nested = Array.isArray(draft?.farm?.imageUrls) ? draft.farm.imageUrls : [];
  return Array.from(new Set([...top, ...nested].filter(Boolean)));
};

const clampText = (value, maxLen = 200) => String(value ?? '').slice(0, maxLen);

const cardSx = {
  borderRadius: '16px',
  border: '1px solid rgba(46,204,122,0.18)',
  boxShadow: '0 8px 32px rgba(10,46,31,0.10)',
  backdropFilter: 'blur(12px)',
  background: 'rgba(255,255,255,0.92)',
};

const inputSx = {
  '& .MuiInputBase-root': { borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontSize: 13 },
  '& .MuiInputLabel-root': { fontFamily: "'DM Sans', sans-serif", fontSize: 13 },
};

const emeraldBtnSx = {
  fontWeight: 700,
  background: 'linear-gradient(135deg, #0F4D33 0%, #1A7A50 55%, #2ECC7A 100%)',
  boxShadow: '0 6px 20px rgba(26,122,80,0.25)',
  borderRadius: '10px',
  textTransform: 'none',
  fontFamily: "'DM Sans', sans-serif",
  py: 0.8,
  '&:hover': { filter: 'brightness(1.05)' },
  '&:disabled': { opacity: 0.5 },
};

const outlineBtnSx = {
  borderRadius: '12px',
  textTransform: 'none',
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 600,
  fontSize: 14,
  py: 1.5,
  borderColor: '#1A7A50',
  color: '#0A2E1F',
  '&:hover': { borderColor: '#2ECC7A', background: 'rgba(46,204,122,0.06)' },
};

const stepIconSx = {
  '& .MuiStepLabel-label': { fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 12 },
  '& .MuiStepIcon-root.Mui-active': { color: '#1A7A50' },
  '& .MuiStepIcon-root.Mui-completed': { color: '#2ECC7A' },
};

const FarmCard = ({ farm }) => {
  const f = farm?.farm || farm;
  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'linear-gradient(135deg, #D1FAE5, #A7F3D0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AgricultureIcon sx={{ color: '#0F4D33', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 17, color: '#0A2E1F' }}>
                {f?.farmName || f?.name || 'Farm'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif" }}>
                Guest Farm
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ borderColor: 'rgba(10,46,31,0.08)' }} />
          {f?.phoneNumber ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneIcon sx={{ color: '#1A7A50', fontSize: 16 }} />
              <Typography sx={{ fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>{f.phoneNumber}</Typography>
            </Stack>
          ) : null}
          {f?.locationDescription ? (
            <Typography sx={{ fontSize: 12, color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif" }}>
              {f.locationDescription}
            </Typography>
          ) : null}
          {(f?.latitude && f?.longitude) ? (
            <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#6B8A7A' }}>
              {Number(f.latitude).toFixed(4)}, {Number(f.longitude).toFixed(4)}
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

const SuccessCard = ({ farmName, phoneNumber, onClose }) => (
  <Box sx={{ textAlign: 'center', py: 2 }}>
    <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #D1FAE5, #A7F3D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
      <CheckCircleIcon sx={{ color: '#0F4D33', fontSize: 36 }} />
    </Box>
    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 22, color: '#0A2E1F', mb: 0.5 }}>
      Farm Added!
    </Typography>
    <Typography sx={{ color: '#6B8A7A', fontSize: 14, fontFamily: "'DM Sans', sans-serif", mb: 2 }}>
      {farmName || 'Guest farm'} has been mapped successfully.
    </Typography>
    {phoneNumber ? (
      <Typography sx={{ color: '#1A7A50', fontSize: 13, fontFamily: "'DM Mono', monospace", mb: 2.5 }}>
        {phoneNumber}
      </Typography>
    ) : null}
    <Button variant="contained" onClick={onClose} sx={emeraldBtnSx}>
      Done
    </Button>
  </Box>
);

const DropletIcon = ({ size = 16, color = '#1A7A50' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C12 2 6 10 6 15C6 18.3137 8.68629 21 12 21C15.3137 21 18 18.3137 18 15C18 10 12 2 12 2Z" fill={color} opacity="0.85"/>
    <path d="M12 18C10.3431 18 9 16.6569 9 15C9 13.3431 12 10 12 10C12 10 15 13.3431 15 15C15 16.6569 13.6569 18 12 18Z" fill="white" opacity="0.4"/>
  </svg>
);

const WaterWaveSvg = ({ size = 16, color = '#3B82F6' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C12 2 6 10 6 15C6 18.3137 8.68629 21 12 21C15.3137 21 18 18.3137 18 15C18 10 12 2 12 2Z" fill={color} opacity="0.8"/>
    <path d="M8 15C8 12 12 8 12 8C12 8 16 12 16 15C16 16.6569 14.2091 18 12 18C9.79086 18 8 16.6569 8 15Z" fill="white" opacity="0.3"/>
  </svg>
);

const LeafSvg = ({ size = 16, color = '#1A7A50' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 8C8 10 3 15 2 21C6 18 12 15 17 8Z" fill={color} opacity="0.9"/>
    <path d="M17 8C14 5 11 4 7 4C7 7 9 9 12 11C14 13 16 15 17 18C18 15 20 10 17 8Z" fill={color} opacity="0.7"/>
    <path d="M17 8L21 3" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export default function GuestFarmDialogs() {
  const [mainOpen, setMainOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addStep, setAddStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const [searchPhone, setSearchPhone] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [editingFarm, setEditingFarm] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDraft, setViewDraft] = useState(null);
  const [draftImageUrls, setDraftImageUrls] = useState([]);
  const [imageOpsBusy, setImageOpsBusy] = useState(false);
  const [imageOpsError, setImageOpsError] = useState('');
  const [uploadTasks, setUploadTasks] = useState([]);
  const filePickerRef = useRef(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [farmName, setFarmName] = useState('');
  const [farmSize, setFarmSize] = useState('');
  const [sizeUnit, setSizeUnit] = useState('acres');
  const [cropName, setCropName] = useState('');

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const googleLibraries = useMemo(() => ['places'], []);
  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useJsApiLoader({ googleMapsApiKey, libraries: googleLibraries });
  const defaultMapCenter = useMemo(() => ({ lat: 20.5937, lng: 78.9629 }), []);

  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationDesc, setLocationDesc] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [basicLocationBusy, setBasicLocationBusy] = useState(false);
  const [boundaryMode, setBoundaryMode] = useState('pin');
  const [farmBoundaryPoints, setFarmBoundaryPoints] = useState([]);

  const [plots, setPlots] = useState([{ id: randomId(), name: 'Plot 1', boundaryPoints: [] }]);
  const [selectedPlotIndex, setSelectedPlotIndex] = useState(0);
  const [mapPlotPoints, setMapPlotPoints] = useState([]);
  const [waterSources, setWaterSources] = useState([]);

  const plotIdOptions = useMemo(() => {
    return plots.map((p) => String(p?.id || '').trim()).filter(Boolean).filter((v, idx, arr) => arr.indexOf(v) === idx);
  }, [plots]);

  const basicMapCenter = useMemo(() => {
    const lat = Number.parseFloat(String(latitude ?? '').trim());
    const lng = Number.parseFloat(String(longitude ?? '').trim());
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return defaultMapCenter;
  }, [latitude, longitude, defaultMapCenter]);

  const mapCenterForPlot = useMemo(() => {
    const pts = mapPlotPoints;
    if (Array.isArray(pts) && pts.length > 0) return pts[pts.length - 1];
    const farmPts = farmBoundaryPoints;
    if (Array.isArray(farmPts) && farmPts.length > 0) return farmPts[farmPts.length - 1];
    return basicMapCenter;
  }, [mapPlotPoints, farmBoundaryPoints, basicMapCenter]);

  const setBasicLocationFromGeocode = useCallback((geocodeResult) => {
    const formatted = geocodeResult?.formatted_address || '';
    setLocationDesc(formatted); setLocationQuery(formatted);
  }, []);

  const reverseGeocodeLatLng = useCallback(async ({ lat, lng }) => {
    if (!mapsLoaded || !window?.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    const res = await geocoder.geocode({ location: { lat, lng } });
    const first = Array.isArray(res?.results) ? res.results[0] : null;
    if (first) setBasicLocationFromGeocode(first);
  }, [mapsLoaded, setBasicLocationFromGeocode]);

  useEffect(() => {
    if (addOpen && plots[selectedPlotIndex]) {
      setMapPlotPoints(Array.isArray(plots[selectedPlotIndex].boundaryPoints) ? plots[selectedPlotIndex].boundaryPoints : []);
    }
  }, [plots, selectedPlotIndex, addOpen]);

  const runLocationAutocomplete = useMemo(() => debounce(async (text) => {
    if (!mapsLoaded || !window?.google?.maps?.places) return;
    const input = String(text || '').trim();
    if (!input) { setLocationSuggestions([]); return; }
    const svc = new window.google.maps.places.AutocompleteService();
    svc.getPlacePredictions({ input, componentRestrictions: { country: 'in' } }, (preds, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) { setLocationSuggestions([]); return; }
      setLocationSuggestions(Array.isArray(preds) ? preds.slice(0, 6) : []);
    });
  }, 250), [mapsLoaded]);

  const selectLocationSuggestion = useCallback(async (prediction) => {
    if (!mapsLoaded || !window?.google?.maps?.places) return;
    const placeId = prediction?.place_id; if (!placeId) return;
    const dummy = document.createElement('div');
    const svc = new window.google.maps.places.PlacesService(dummy);
    setBasicLocationBusy(true);
    try {
      const details = await new Promise((resolve, reject) => {
        svc.getDetails({ placeId, fields: ['geometry.location', 'formatted_address', 'address_components'] }, (result, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK) { reject(new Error(`Places details failed: ${String(status)}`)); return; }
          resolve(result);
        });
      });
      const loc = details?.geometry?.location;
      const lat = typeof loc?.lat === 'function' ? loc.lat() : null;
      const lng = typeof loc?.lng === 'function' ? loc.lng() : null;
      if (lat == null || lng == null) return;
      setLatitude(String(lat)); setLongitude(String(lng));
      setLocationSuggestions([]);
      setBasicLocationFromGeocode({ formatted_address: details?.formatted_address, address_components: details?.address_components });
    } finally { setBasicLocationBusy(false); }
  }, [mapsLoaded, setBasicLocationFromGeocode]);

  const useCurrentLocation = useCallback(async () => {
    if (!navigator?.geolocation) { setError('Geolocation is not available in this browser.'); return; }
    setError(''); setBasicLocationBusy(true);
    try {
      const pos = await new Promise((resolve, reject) => { navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000 }); });
      const lat = pos?.coords?.latitude; const lng = pos?.coords?.longitude;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('Unable to read GPS coordinates.');
      setLatitude(String(lat)); setLongitude(String(lng));
      await reverseGeocodeLatLng({ lat, lng });
    } catch (e) { setError(e?.message || 'Failed to read current location.'); }
    finally { setBasicLocationBusy(false); }
  }, [reverseGeocodeLatLng]);

  const resetAddForm = () => {
    setAddStep(0);
    setPhoneNumber('');
    setFarmName('');
    setFarmSize('');
    setSizeUnit('acres');
    setCropName('');
    setLocationQuery('');
    setLocationSuggestions([]);
    setLocationDesc('');
    setLatitude('');
    setLongitude('');
    setBoundaryMode('pin');
    setFarmBoundaryPoints([]);
    setSelectedPlotIndex(0);
    setMapPlotPoints([]);
    setPlots([{ id: randomId(), name: 'Plot 1', boundaryPoints: [] }]);
    setWaterSources([]);
    setError('');
    setSuccess('');
    setShowSuccess(false);
    setSubmitting(false);
    setEditingFarm(null);
    setDraftImageUrls([]);
    setUploadTasks([]);
    setImageOpsError('');
  };

  const handleSearch = async () => {
    const digits = searchPhone.replace(/\D/g, '');
    if (!digits) { setSearchError('Enter a phone number'); return; }
    setSearching(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const res = await searchMapDrafts(baseURL, { phoneNumber: digits });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.message || json?.code || 'Search failed');
      const drafts = json?.data?.drafts || [];
      if (drafts.length === 0) { setSearchError('No farm found with this phone number.'); return; }
      setSearchResult(drafts[0]);
    } catch (e) {
      setSearchError(e?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async () => {
    if (!phoneNumber) { setError('Phone number is required.'); return; }
    if (!farmName) { setError('Farm name is required.'); return; }
    const lat = Number.parseFloat(String(latitude ?? '').trim());
    const lng = Number.parseFloat(String(longitude ?? '').trim());
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !locationDesc) { setError('Farm location is required. Search a place and pin the exact point on the map.'); return; }
    const boundary = normalizeBoundaryPoints(farmBoundaryPoints);
    if (boundary.length < 3) { setError('Farm boundary is required. Draw the farm boundary on the map (at least 3 points).'); return; }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const draftId = editingFarm?._id || editingFarm?.id || null;
      const payload = {
        ...(draftId ? { _id: draftId } : {}),
        phoneNumber,
        farm: {
          farmName,
          phoneNumber,
          locationDescription: locationDesc,
          latitude: lat,
          longitude: lng,
          boundary,
          ...(farmSize ? { size: Number.parseFloat(farmSize) } : {}),
          ...(sizeUnit ? { sizeUnit } : {}),
          ...(cropName ? { cropName } : {}),
          ...(draftImageUrls.length ? { imageUrls: draftImageUrls } : {}),
        },
        plots: plots.map((p) => ({
          id: p.id,
          name: p.name,
          ...(Array.isArray(p.boundaryPoints) && p.boundaryPoints.length >= 3 ? { boundary: normalizeBoundaryPoints(p.boundaryPoints) } : {}),
        })),
        waterSources: waterSources.map((w) => {
          const plotIds = Array.isArray(w?.plotIds) ? w.plotIds.filter(Boolean) : [];
          return {
            id: w.id,
            name: w.name,
            type: w.type,
            ...(plotIds.length ? { plotIds } : {}),
            ...(w.lat && w.lng ? { location: { lat: Number.parseFloat(w.lat), lng: Number.parseFloat(w.lng) } } : {}),
          };
        }),
        progress: {
          locationDone: true,
          plotsDone: plots.some((p) => normalizeBoundaryPoints(p.boundaryPoints).length >= 3),
          waterSourcesDone: waterSources.some((w) => w.lat && w.lng),
        },
      };
      const res = await upsertMapDraft(baseURL, payload);
      const rawText = await res.text().catch(() => '');
      const json = rawText ? (() => { try { return JSON.parse(rawText); } catch (_) { return null; } })() : null;
      if (!res.ok) {
        const msg = json?.message || json?.error || json?.code || rawText || `Failed (${res.status})`;
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      setShowSuccess(true);
    } catch (e) {
      setError(e?.message || 'Failed to save farm');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchPhone('');
    setSearchResult(null);
    setSearchError('');
    setEditingFarm(null);
  };

  const handleAddClose = () => {
    setAddOpen(false);
    resetAddForm();
  };

  const populateFormFromDraft = useCallback((draft) => {
    if (!draft) return;
    const f = draft?.farm || draft;
    setPhoneNumber(f?.phoneNumber || '');
    setFarmName(f?.farmName || f?.name || '');
    setFarmSize(f?.size != null ? String(f.size) : '');
    setSizeUnit(f?.sizeUnit || 'acres');
    setCropName(f?.cropName || '');
    setLocationDesc(f?.locationDescription || '');
    setLocationQuery(f?.locationDescription || '');
    setLatitude(f?.latitude != null ? String(f.latitude) : '');
    setLongitude(f?.longitude != null ? String(f.longitude) : '');
    const boundarySrc = draft?.boundary || f?.boundary;
    if (Array.isArray(boundarySrc)) {
      setFarmBoundaryPoints(boundarySrc);
    }
    if (Array.isArray(draft?.plots) && draft.plots.length > 0) {
      setPlots(draft.plots.map((p) => ({
        id: p.id || randomId(),
        name: p.name || 'Plot',
        boundaryPoints: Array.isArray(p.boundary) ? p.boundary : Array.isArray(p.boundaryPoints) ? p.boundaryPoints : [],
      })));
      setSelectedPlotIndex(0);
      const firstPlot = draft.plots[0];
      setMapPlotPoints(Array.isArray(firstPlot?.boundary) ? firstPlot.boundary : Array.isArray(firstPlot?.boundaryPoints) ? firstPlot.boundaryPoints : []);
    }
    if (Array.isArray(draft?.waterSources) && draft.waterSources.length > 0) {
      setWaterSources(draft.waterSources.map((w) => ({
        id: w.id || `ws-${Math.random()}`,
        name: w.name || 'Water source',
        type: w.type || 'Motor',
        plotIds: Array.isArray(w?.plotIds) ? w.plotIds.filter(Boolean) : (w?.plotId ? [String(w.plotId)].filter(Boolean) : []),
        lat: w.location?.lat != null ? String(w.location.lat) : w.lat || '',
        lng: w.location?.lng != null ? String(w.location.lng) : w.lng || '',
        pumpHp: w.pumpHp || '',
      })));
    }
  }, []);

  const handleEditSearchResult = useCallback(async () => {
    if (!searchResult) return;
    const f = searchResult?.farm || searchResult;
    const phone = f?.phoneNumber || '';
    if (!phone) { setSearchError('No phone number on record.'); return; }
    setSearching(true);
    setSearchError('');
    try {
      const res = await getMapDraft(baseURL, { phoneNumber: phone });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.message || json?.code || 'Failed to fetch farm details');
      const draft = json?.data?.draft || json?.data || json;
      if (!draft) throw new Error('Farm details not found');
      setEditingFarm(draft);
      populateFormFromDraft(draft);
      setDraftImageUrls(getDraftImageUrls(draft));
      setSearchOpen(false);
      setAddOpen(true);
      setAddStep(0);
    } catch (e) {
      setSearchError(e?.message || 'Failed to load farm for editing');
    } finally {
      setSearching(false);
    }
  }, [searchResult, populateFormFromDraft]);

  const handleViewDetails = useCallback(async () => {
    if (!searchResult) return;
    const f = searchResult?.farm || searchResult;
    const phone = f?.phoneNumber || '';
    if (!phone) { setSearchError('No phone number on record.'); return; }
    setSearching(true);
    setSearchError('');
    try {
      const res = await getMapDraft(baseURL, { phoneNumber: phone });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.message || json?.code || 'Failed to fetch farm details');
      const draft = json?.data?.draft || json?.data || json;
      if (!draft) throw new Error('Farm details not found');
      setViewDraft(draft);
      setDraftImageUrls(getDraftImageUrls(draft));
      setViewOpen(true);
    } catch (e) {
      setSearchError(e?.message || 'Failed to load farm details');
    } finally {
      setSearching(false);
    }
  }, [searchResult]);

  const persistDraftImages = async (nextUrls) => {
    const urls = Array.from(new Set(nextUrls.filter(Boolean)));
    if (viewOpen && viewDraft) {
      const vf = viewDraft?.farm || viewDraft;
      const phone = vf?.phoneNumber || '';
      if (!phone) return;
      const payload = {
        phoneNumber: phone,
        farm: { ...vf, imageUrls: urls },
        ...(Array.isArray(viewDraft?.plots) ? { plots: viewDraft.plots } : {}),
        ...(Array.isArray(viewDraft?.waterSources) ? { waterSources: viewDraft.waterSources } : {}),
        progress: { locationDone: true, plotsDone: true, waterSourcesDone: true },
      };
      const res = await upsertMapDraft(baseURL, payload);
      if (!res.ok) throw new Error('Failed to save images');
      const json = await res.json().catch(() => null);
      const updated = json?.data?.draft || json?.data || null;
      if (updated) { setViewDraft(updated); setDraftImageUrls(getDraftImageUrls(updated)); }
    } else if (phoneNumber) {
      const payload = { phoneNumber, farm: { phoneNumber, imageUrls: urls }, progress: {} };
      const res = await upsertMapDraft(baseURL, payload);
      if (!res.ok) throw new Error('Failed to save images');
      setDraftImageUrls(urls);
    }
  };

  const beginUploads = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setImageOpsBusy(true);
    setImageOpsError('');
    const tasks = list.map((f) => ({ id: `img-${Date.now()}-${Math.random().toString(16).slice(2)}`, name: String(f?.name || 'image'), size: Number(f?.size || 0), progress: 0, status: 'uploading' }));
    setUploadTasks((prev) => [...tasks, ...prev]);
    try {
      let nextUrls = Array.from(new Set(draftImageUrls.filter(Boolean)));
      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        const taskId = tasks[i]?.id;
        const result = await uploadFarmImage(baseURL, file, {
          onProgress: (pct) => setUploadTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, progress: pct } : t))),
        });
        const uploaded = Array.isArray(result?.json?.data?.imageUrls) ? result.json.data.imageUrls : [];
        if (uploaded.length === 0) throw new Error('Upload succeeded but no image URL returned.');
        nextUrls = Array.from(new Set([...nextUrls, ...uploaded].filter(Boolean)));
        setDraftImageUrls(nextUrls);
        setUploadTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, progress: 100, status: 'done' } : t)));
        await persistDraftImages(nextUrls);
      }
    } catch (e) {
      setImageOpsError(e?.message || 'Failed to upload images.');
      setUploadTasks((prev) => prev.map((t) => (t.status === 'uploading' ? { ...t, status: 'error' } : t)));
    } finally {
      setImageOpsBusy(false);
      if (filePickerRef.current) filePickerRef.current.value = '';
    }
  };

  const handleDeleteDraftImage = async (url) => {
    const u = String(url || '').trim();
    if (!u) return;
    setImageOpsBusy(true);
    setImageOpsError('');
    try {
      const res = await deleteFarmImage(baseURL, u);
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.message || json?.code || `Delete failed (${res.status})`);
      const nextUrls = draftImageUrls.filter((x) => String(x) !== u);
      setDraftImageUrls(nextUrls);
      await persistDraftImages(nextUrls);
    } catch (e) {
      setImageOpsError(e?.message || 'Failed to delete image.');
    } finally {
      setImageOpsBusy(false);
    }
  };

  const handleOptionSearch = () => {
    setMainOpen(false);
    setSearchOpen(true);
  };

  const handleOptionAdd = () => {
    setMainOpen(false);
    setAddOpen(true);
  };

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        onClick={() => setMainOpen(true)}
        startIcon={<MapIcon />}
        sx={{
          mt: 1.5,
          py: 1.1,
          fontWeight: 700,
          borderRadius: '10px',
          textTransform: 'none',
          fontFamily: "'DM Sans', sans-serif",
          borderColor: '#1A7A50',
          color: '#1A7A50',
          fontSize: 14,
          '&:hover': {
            borderColor: '#2ECC7A',
            background: 'rgba(46,204,122,0.08)',
          },
        }}
      >
        Map a farm
      </Button>

      {/* Main options dialog */}
      <Dialog open={mainOpen} onClose={() => setMainOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px', border: '1px solid rgba(46,204,122,0.20)', boxShadow: '0 24px 80px rgba(10,46,31,0.20)' } }}>
        <DialogTitle sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#0A2E1F', fontSize: 20, pb: 0 }}>
          Map a Farm
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif", fontSize: 14, mt: 1, mb: 2.5 }}>
            Choose an option to get started.
          </Typography>
          <Stack spacing={1.5}>
            <Button fullWidth variant="outlined" onClick={handleOptionSearch} sx={outlineBtnSx}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                <SearchIcon sx={{ color: '#1A7A50' }} />
                <span>Search your farm</span>
              </Stack>
            </Button>
            <Button fullWidth variant="outlined" onClick={handleOptionAdd} sx={outlineBtnSx}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                <AddLocationIcon sx={{ color: '#1A7A50' }} />
                <span>Map a new farm</span>
              </Stack>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setMainOpen(false)} sx={{ textTransform: 'none', fontFamily: "'DM Sans', sans-serif", color: '#6B8A7A' }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Search farm dialog */}
      <Dialog open={searchOpen} onClose={handleSearchClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', border: '1px solid rgba(46,204,122,0.20)', boxShadow: '0 24px 80px rgba(10,46,31,0.20)' } }}>
        <DialogTitle sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#0A2E1F', fontSize: 20, pb: 0 }}>
          Search your farm
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif", fontSize: 14, mt: 1, mb: 2 }}>
            Enter your phone number to find your farm.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Phone number *"
              value={searchPhone}
              onChange={(e) => { setSearchPhone(e.target.value); setSearchError(''); setSearchResult(null); }}
              placeholder="Enter registered phone number"
              fullWidth
              type="tel"
              required
              inputProps={{ inputMode: 'numeric' }}
              sx={inputSx}
              InputProps={{
                startAdornment: <PhoneIcon sx={{ color: 'rgba(10,46,31,0.35)', mr: 1, fontSize: 18 }} />,
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={searching || !searchPhone.trim()}
              sx={emeraldBtnSx}
            >
              {searching ? <CircularProgress size={20} color="inherit" /> : 'Search'}
            </Button>
            {searchError ? <Alert severity="error" sx={{ borderRadius: '10px', fontFamily: "'DM Sans', sans-serif" }}>{searchError}</Alert> : null}
            {searchResult ? (
              <Stack spacing={1.5}>
                <FarmCard farm={searchResult} />
                <Button variant="outlined" fullWidth onClick={handleViewDetails} disabled={searching} sx={outlineBtnSx}>
                  View details
                </Button>
                <Button variant="contained" fullWidth onClick={handleEditSearchResult} sx={emeraldBtnSx}>
                  Edit this farm
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleSearchClose} sx={{ textTransform: 'none', fontFamily: "'DM Sans', sans-serif", color: '#6B8A7A' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add farm dialog */}
      <Dialog open={addOpen} onClose={(_, reason) => { if (reason !== 'backdropClick') handleAddClose(); }} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '20px', border: '1px solid rgba(46,204,122,0.20)', boxShadow: '0 24px 80px rgba(10,46,31,0.20)' } }}>
        <DialogTitle sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#0A2E1F', fontSize: 20, pb: 0 }}>
          {showSuccess ? '' : editingFarm ? 'Edit Farm' : 'Map a New Farm'}
        </DialogTitle>
        <DialogContent>
          {showSuccess ? (
            <SuccessCard farmName={farmName} phoneNumber={phoneNumber} onClose={handleAddClose} />
          ) : (
            <>
              <Typography sx={{ color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif", fontSize: 14, mt: 1, mb: 2 }}>
                {editingFarm ? 'Edit the guest farm details below.' : 'Fill in the details to map a guest farm.'}
              </Typography>

              {/* Stepper */}
              <Box sx={{ mb: 3, background: 'linear-gradient(135deg, rgba(167,243,208,0.15) 0%, rgba(255,255,255,0.95) 100%)', borderRadius: '16px', p: 2, border: '1px solid rgba(46,204,122,0.15)' }}>
                <Stepper activeStep={addStep}>
                  {['Basic Info', 'Plots', 'Water Sources'].map((label) => (
                    <Step key={label}>
                      <StepLabel sx={stepIconSx}>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily: "'DM Sans', sans-serif" }}>{error}</Alert> : null}
              {success ? <Alert severity="success" sx={{ mb: 2, borderRadius: '10px', fontFamily: "'DM Sans', sans-serif" }}>{success}</Alert> : null}

              {/* Step 0 - Basic Info */}
              {addStep === 0 ? (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 2.5 }}>
                    <Grid item xs={12} md={5}>
                      <Stack spacing={2}>
                        <TextField label="Phone number *" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))} placeholder="Farm owner's phone number" fullWidth type="tel" disabled={!!editingFarm} inputProps={{ inputMode: 'numeric' }} sx={inputSx}
                          InputProps={{ startAdornment: <PhoneIcon sx={{ color: 'rgba(10,46,31,0.35)', mr: 1, fontSize: 18 }} /> }}
                        />
                        <TextField label="Farm name *" value={farmName} onChange={(e) => setFarmName(e.target.value)} placeholder="e.g. Green Valley Farm" fullWidth sx={inputSx}
                          InputProps={{ startAdornment: <AgricultureIcon sx={{ color: 'rgba(10,46,31,0.35)', mr: 1, fontSize: 18 }} /> }}
                        />

                        {/* Farm Details */}
                        <Box sx={{ background: 'rgba(255,255,255,0.90)', borderRadius: '16px', border: '1px solid rgba(10,46,31,0.09)', p: 2.5, boxShadow: '0 2px 16px rgba(10,46,31,0.06)' }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <LeafSvg size={16} color="#1A7A50" />
                            <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>Farm Details</Typography>
                          </Stack>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField label="Farm size" value={farmSize} onChange={(e) => setFarmSize(e.target.value)} type="number" fullWidth size="small" sx={inputSx} />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField label="Unit" value={sizeUnit} onChange={(e) => setSizeUnit(e.target.value)} select fullWidth size="small" sx={inputSx}>
                                <MenuItem value="acres">Acres</MenuItem>
                                <MenuItem value="hectare">Hectare</MenuItem>
                                <MenuItem value="sqft">Sqft</MenuItem>
                                <MenuItem value="sqmt">Sqmt</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField label="Crop name" value={cropName} onChange={(e) => setCropName(e.target.value)} placeholder="e.g. Sugarcane" fullWidth size="small" sx={inputSx} />
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Farm Location */}
                        <Box sx={{ background: 'rgba(255,255,255,0.90)', borderRadius: '16px', border: '1px solid rgba(10,46,31,0.09)', p: 2.5, boxShadow: '0 2px 16px rgba(10,46,31,0.06)' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <DropletIcon size={16} color="#1A7A50" />
                              <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>Farm Location *</Typography>
                            </Stack>
                            <Button variant="outlined" size="small" onClick={useCurrentLocation} disabled={basicLocationBusy || submitting}
                              sx={{ borderRadius: '999px', textTransform: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, borderColor: '#1A7A50', color: '#1A7A50' }}>
                              {basicLocationBusy ? 'Locating\u2026' : 'Use GPS'}
                            </Button>
                          </Stack>
                          <TextField
                            label="Search location"
                            value={locationQuery}
                            onChange={(e) => { const next = e.target.value; setLocationQuery(next); runLocationAutocomplete(next); }}
                            placeholder="Village / city\u2026"
                            fullWidth
                            size="small"
                            sx={inputSx}
                          />
                          {locationSuggestions.length ? (
                            <Box sx={{ mt: 1, background: 'white', borderRadius: '12px', border: '1px solid rgba(10,46,31,0.12)', overflow: 'hidden', boxShadow: '0 4px 20px rgba(10,46,31,0.12)' }}>
                              <List disablePadding dense>
                                {locationSuggestions.map((p) => (
                                  <ListItemButton key={p.place_id} onClick={() => selectLocationSuggestion(p)} disabled={basicLocationBusy || submitting} sx={{ py: 1, px: 1.5, '&:hover': { background: 'rgba(46,204,122,0.06)' } }}>
                                    <ListItemText primary={clampText(p.description, 90)} secondary={p.structured_formatting?.secondary_text || ''} primaryTypographyProps={{ sx: { fontSize: 13, fontFamily: "'DM Sans', sans-serif", fontWeight: 500 } }} secondaryTypographyProps={{ sx: { fontSize: 11, fontFamily: "'DM Mono', monospace" } }} />
                                  </ListItemButton>
                                ))}
                              </List>
                            </Box>
                          ) : null}
                          <Box sx={{ mt: 1 }}>
                            <TextField
                              label="Address"
                              value={locationDesc}
                              onChange={(e) => setLocationDesc(e.target.value)}
                              fullWidth
                              size="small"
                              placeholder="Pin on map to fill address"
                              sx={inputSx}
                            />
                            {Number.isFinite(Number.parseFloat(String(latitude))) && Number.isFinite(Number.parseFloat(String(longitude))) ? (
                              <Typography sx={{ mt: 0.75, fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#6B8A7A' }}>
                                PINNED: {formatLatLngCompact(latitude, longitude, 6)}
                              </Typography>
                            ) : (
                              <Typography sx={{ mt: 0.75, fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#6B8A7A' }}>
                                Tip: search a place, then tap the map to pin the exact spot.
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 460 }}>
                        {/* Farm boundary toggle */}
                        <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexShrink: 0 }}>
                          <Button
                            size="small"
                            variant={boundaryMode === 'pin' ? 'contained' : 'outlined'}
                            onClick={() => setBoundaryMode('pin')}
                            sx={{
                              borderRadius: '8px', textTransform: 'none', fontSize: 11,
                              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, py: 0.4, px: 1.5,
                              ...(boundaryMode === 'pin'
                                ? { background: 'linear-gradient(135deg, #0A2E1F, #1A7A50)', color: 'white' }
                                : { borderColor: '#1A7A50', color: '#1A7A50' }),
                            }}
                          >
                            Pin Location
                          </Button>
                          <Button
                            size="small"
                            variant={boundaryMode === 'farm' ? 'contained' : 'outlined'}
                            onClick={() => setBoundaryMode('farm')}
                            sx={{
                              borderRadius: '8px', textTransform: 'none', fontSize: 11,
                              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, py: 0.4, px: 1.5,
                              ...(boundaryMode === 'farm'
                                ? { background: 'linear-gradient(135deg, #0A2E1F, #1A7A50)', color: 'white' }
                                : { borderColor: '#1A7A50', color: '#1A7A50' }),
                            }}
                          >
                            Farm Boundary
                          </Button>
                        </Stack>
                        {/* Farm boundary drawing controls */}
                        {boundaryMode === 'farm' ? (
                          <Box sx={{ mb: 1, flexShrink: 0, background: 'rgba(255,255,255,0.95)', borderRadius: '10px', border: '1.5px solid rgba(10,46,31,0.20)', p: 1.5, boxShadow: '0 2px 8px rgba(10,46,31,0.06)' }}>
                            <Stack spacing={0.75}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Box sx={{ width: 18, height: 18, borderRadius: '4px', background: 'linear-gradient(135deg, #0A2E1F, #1A7A50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <LeafSvg size={10} color="#A7F3D0" />
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 11, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>Farm Boundary</Typography>
                                <Typography sx={{ fontSize: 9, fontFamily: "'DM Mono', monospace", color: '#6B8A7A', ml: 1 }}>{farmBoundaryPoints.length} pts</Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.5}>
                                <Button size="small" variant="outlined" disabled={!farmBoundaryPoints.length} onClick={() => setFarmBoundaryPoints((prev) => prev.slice(0, -1))} sx={{ borderRadius: '6px', textTransform: 'none', fontSize: 10, fontFamily: "'DM Sans', sans-serif", py: 0.3, px: 1.5 }}>Undo</Button>
                                <Button size="small" variant="outlined" disabled={!farmBoundaryPoints.length} onClick={() => setFarmBoundaryPoints([])} sx={{ borderRadius: '6px', textTransform: 'none', fontSize: 10, fontFamily: "'DM Sans', sans-serif", py: 0.3, px: 1.5 }}>Clear</Button>
                                <Button size="small" variant="contained" disabled={normalizeBoundaryPoints(farmBoundaryPoints).length < 3}
                                  onClick={() => { setFarmBoundaryPoints(normalizeBoundaryPoints(farmBoundaryPoints)); setSuccess('Farm boundary saved.'); setError(''); }}
                                  sx={{ borderRadius: '6px', fontSize: '10px', py: 0.3, px: 1.5, background: 'linear-gradient(135deg, #0F4D33, #1A7A50)', color: 'white', '&:hover': { filter: 'brightness(1.05)' } }}>
                                  Save Boundary
                                </Button>
                              </Stack>
                            </Stack>
                          </Box>
                        ) : null}
                        <Box className="map-wrapper" sx={{ flex: 1, minHeight: 350 }}>
                          {!googleMapsApiKey ? <Alert severity="warning">Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code>.</Alert>
                            : mapsLoadError ? <Alert severity="error">Maps failed to load.</Alert>
                            : !mapsLoaded ? <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress sx={{ color: '#1A7A50' }} /></Box>
                            : (
                              <GoogleMap
                                mapContainerStyle={{ width: '100%', height: '100%', minHeight: 350 }}
                                center={basicMapCenter}
                                zoom={Number.isFinite(Number.parseFloat(latitude)) ? 16 : 5}
                                options={{ streetViewControl: false, mapTypeControl: true, fullscreenControl: false }}
                                onClick={async (e) => {
                                  const lat = e?.latLng?.lat?.();
                                  const lng = e?.latLng?.lng?.();
                                  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                                  if (boundaryMode === 'farm') {
                                    setFarmBoundaryPoints((prev) => [...prev, { lat, lng }]);
                                  } else {
                                    setLatitude(String(lat));
                                    setLongitude(String(lng));
                                    await reverseGeocodeLatLng({ lat, lng });
                                  }
                                }}
                              >
                                {Number.isFinite(Number.parseFloat(latitude)) && Number.isFinite(Number.parseFloat(longitude)) ? (
                                  <Marker position={basicMapCenter} draggable onDragEnd={async (e) => {
                                    const lat = e?.latLng?.lat?.();
                                    const lng = e?.latLng?.lng?.();
                                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                                    if (boundaryMode === 'farm') return;
                                    setLatitude(String(lat));
                                    setLongitude(String(lng));
                                    await reverseGeocodeLatLng({ lat, lng });
                                  }} />
                                ) : null}
                                {normalizeBoundaryPoints(farmBoundaryPoints).length >= 2 ? (
                                  <Polygon
                                    path={normalizeBoundaryPoints(farmBoundaryPoints)}
                                    options={{
                                      strokeColor: '#0A2E1F',
                                      strokeOpacity: 0.85,
                                      strokeWeight: 3.5,
                                      fillColor: '#1A7A50',
                                      fillOpacity: 0.08,
                                    }}
                                  />
                                ) : null}
                                {farmBoundaryPoints.map((p, idx) => (
                                  <Marker key={`farm-${p.lat}-${p.lng}-${idx}`} position={toLatLngLiteral(p) || undefined}
                                    icon={{
                                      url: makeSvgMarkerUrl(
                                        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="#0A2E1F" stroke="#fff" stroke-width="2"/></svg>'
                                      ),
                                      scaledSize: typeof window !== 'undefined' && window?.google?.maps ? new window.google.maps.Size(16, 16) : undefined,
                                      anchor: typeof window !== 'undefined' && window?.google?.maps ? new window.google.maps.Point(8, 8) : undefined,
                                    }}
                                  />
                                ))}
                              </GoogleMap>
                            )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Stack direction="row" justifyContent="flex-end">
                    <Button variant="contained" onClick={() => setAddStep(1)} disabled={!phoneNumber || !farmName || !locationDesc || !Number.isFinite(Number.parseFloat(latitude)) || !Number.isFinite(Number.parseFloat(longitude)) || normalizeBoundaryPoints(farmBoundaryPoints).length < 3} sx={emeraldBtnSx}>
                      Next – Plots →
                    </Button>
                  </Stack>
                </Box>
              ) : null}

              {/* Step 1 - Plots */}
              {addStep === 1 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {/* Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LeafSvg size={16} color="#1A7A50" />
                      <Typography sx={{ fontWeight: 700, fontSize: 15, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>Plot Boundaries</Typography>
                    </Stack>
                    <Button variant="outlined" onClick={() => { setPlots((prev) => [...prev, { id: randomId(), name: `Plot ${prev.length + 1}`, boundaryPoints: [] }]); setSelectedPlotIndex(plots.length); }}
                      sx={{ borderRadius: '999px', textTransform: 'none', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, borderColor: '#1A7A50', color: '#1A7A50' }}>
                      + Add Plot
                    </Button>
                  </Stack>

                  {mapsLoadError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', flexShrink: 0 }}>Google Maps failed to load.</Alert>}
                  {!googleMapsApiKey && <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px', flexShrink: 0 }}>Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code>.</Alert>}

                  {/* Form Controls Area */}
                  <Box sx={{ mb: 2, flexShrink: 0, display: 'flex', gap: 2, overflow: 'auto' }}>
                    {/* Plot List */}
                    <Box sx={{ minWidth: 'fit-content', display: 'flex', gap: 1, pb: 1 }}>
                      {plots.map((plot, idx) => (
                        <Box key={plot.id || idx} sx={{ background: selectedPlotIndex === idx ? 'linear-gradient(135deg, rgba(167,243,208,0.25) 0%, rgba(255,255,255,0.95) 100%)' : 'rgba(255,255,255,0.90)', borderRadius: '12px', border: selectedPlotIndex === idx ? '1.5px solid rgba(46,204,122,0.40)' : '1px solid rgba(10,46,31,0.09)', p: 1.5, boxShadow: '0 2px 8px rgba(10,46,31,0.06)', transition: 'all 0.2s', minWidth: 240 }}>
                          <Stack spacing={0.75}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box sx={{ width: 24, height: 24, borderRadius: '6px', background: 'linear-gradient(135deg, #D1FAE5, #A7F3D0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 900, color: '#0F4D33', fontFamily: "'DM Mono', monospace" }}>{idx + 1}</Typography>
                              </Box>
                              <TextField label="ID" value={plot.id} onChange={(e) => setPlots((prev) => prev.map((p, i) => (i === idx ? { ...p, id: e.target.value } : p)))} size="small" sx={{ flex: 1, '& .MuiInputBase-root': { borderRadius: '6px', fontFamily: "'DM Mono', monospace", fontSize: 11 } }} />
                            </Stack>
                            <TextField label="Name" value={plot.name} onChange={(e) => setPlots((prev) => prev.map((p, i) => (i === idx ? { ...p, name: e.target.value } : p)))} size="small" fullWidth sx={{ '& .MuiInputBase-root': { borderRadius: '6px', fontSize: 11 } }} />
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
                              <Typography sx={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: '#6B8A7A' }}>{Array.isArray(plot.boundaryPoints) ? plot.boundaryPoints.length : 0} pts</Typography>
                              <Stack direction="row" spacing={0.5}>
                                <Button size="small" variant={selectedPlotIndex === idx ? 'contained' : 'outlined'} onClick={() => setSelectedPlotIndex(idx)}
                                  sx={{ borderRadius: '6px', textTransform: 'none', fontSize: 10, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, py: 0.3, px: 1, ...(selectedPlotIndex === idx ? { background: 'linear-gradient(135deg, #0A2E1F, #1A7A50)', color: 'white' } : { borderColor: '#1A7A50', color: '#1A7A50' }) }}>
                                  Edit
                                </Button>
                                <Button size="small" color="error" onClick={() => { setPlots((prev) => prev.filter((_, i) => i !== idx)); setSelectedPlotIndex((prevIdx) => (prevIdx === idx ? 0 : prevIdx > idx ? prevIdx - 1 : prevIdx)); }} disabled={plots.length <= 1}
                                  sx={{ borderRadius: '6px', textTransform: 'none', fontSize: 10, fontFamily: "'DM Sans', sans-serif", py: 0.3, px: 1 }}>
                                  Remove
                                </Button>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                    </Box>

                    {/* Plot Drawing Controls */}
                    <Box sx={{ minWidth: 'fit-content', display: 'flex', gap: 1, alignItems: 'flex-end', pb: 1 }}>
                      <Box sx={{ background: 'rgba(255,255,255,0.90)', borderRadius: '12px', border: '1px solid rgba(10,46,31,0.09)', p: 1.5, boxShadow: '0 2px 8px rgba(10,46,31,0.06)' }}>
                        <Stack spacing={0.75}>
                          <Typography sx={{ fontWeight: 600, fontSize: 11, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>{plots[selectedPlotIndex]?.name || `Plot ${selectedPlotIndex + 1}`}</Typography>
                          <Stack direction="row" spacing={0.5}>
                            <Button size="small" variant="outlined" disabled={!mapPlotPoints.length} onClick={() => setMapPlotPoints((prev) => prev.slice(0, -1))} sx={{ borderRadius: '6px', textTransform: 'none', fontSize: 10, fontFamily: "'DM Sans', sans-serif", py: 0.3, px: 1 }}>Undo</Button>
                            <Button size="small" variant="outlined" disabled={!mapPlotPoints.length} onClick={() => setMapPlotPoints([])} sx={{ borderRadius: '6px', textTransform: 'none', fontSize: 10, fontFamily: "'DM Sans', sans-serif", py: 0.3, px: 1 }}>Clear</Button>
                            <Button size="small" variant="contained" disabled={normalizeBoundaryPoints(mapPlotPoints).length < 3}
                              onClick={() => { setPlots((prev) => prev.map((p, i) => i === selectedPlotIndex ? { ...p, boundaryPoints: normalizeBoundaryPoints(mapPlotPoints) } : p)); setSuccess('Boundary saved. Click "Save Plots" to sync.'); setError(''); }}
                              sx={{ borderRadius: '6px', fontSize: '10px', py: 0.3, px: 1.5, background: 'linear-gradient(135deg, #0F4D33, #1A7A50)', color: 'white', '&:hover': { filter: 'brightness(1.05)' } }}>
                              Save
                            </Button>
                          </Stack>
                          <Typography sx={{ fontSize: 9, fontFamily: "'DM Mono', monospace", color: '#6B8A7A', letterSpacing: '0.02em' }}>TAP MAP TO ADD VERTICES</Typography>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>

                  {/* Map Area - Takes Remaining Space */}
                  {googleMapsApiKey && mapsLoaded ? (
                    <Box className="map-wrapper" sx={{ flex: 1, overflow: 'hidden', minHeight: 300, mb: 2 }}>
                      <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%', minHeight: 350 }}
                        center={mapCenterForPlot} zoom={18} mapTypeId="hybrid"
                        onClick={(e) => { const lat = e?.latLng?.lat?.(); const lng = e?.latLng?.lng?.(); if (!Number.isFinite(lat) || !Number.isFinite(lng)) return; setMapPlotPoints((prev) => [...prev, { lat, lng }]); }}
                      >
                        {mapPlotPoints.length >= 2 ? <Polygon path={mapPlotPoints} options={{ strokeColor: '#2ECC7A', strokeOpacity: 1, strokeWeight: 2.5, fillColor: '#2ECC7A', fillOpacity: 0.20 }} /> : null}
                        {mapPlotPoints.map((p, idx) => <Marker key={`${p.lat}-${p.lng}-${idx}`} position={toLatLngLiteral(p) || undefined} />)}
                      </GoogleMap>
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(167,243,208,0.10)', borderRadius: '12px', minHeight: 300, mb: 2 }}>
                      <Typography sx={{ color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif" }}>Map not available.</Typography>
                    </Box>
                  )}

                  {/* Footer Actions */}
                  <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ flexShrink: 0 }}>
                    <Button variant="text" onClick={() => setAddStep(0)} sx={{ textTransform: 'none', fontFamily: "'DM Sans', sans-serif", color: '#6B8A7A' }}>← Back</Button>
                    <Button variant="contained" onClick={() => setAddStep(2)} disabled={plots.length === 0 || !plots.some((p) => normalizeBoundaryPoints(p.boundaryPoints).length >= 3)} sx={emeraldBtnSx}>Next – Water Sources →</Button>
                  </Stack>
                </Box>
              ) : null}

              {/* Step 2 - Water Sources */}
              {addStep === 2 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {/* Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DropletIcon size={16} color="#1A7A50" />
                      <Typography sx={{ fontWeight: 700, fontSize: 15, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>Water Sources</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#6B8A7A', letterSpacing: '0.06em' }}>TAP MAP TO ADD</Typography>
                  </Stack>

                  {mapsLoadError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', flexShrink: 0 }}>Google Maps failed to load.</Alert>}
                  {!googleMapsApiKey && <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px', flexShrink: 0 }}>Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code>.</Alert>}

                  {/* Form Controls Area - Horizontal Scrollable */}
                  <Box sx={{ mb: 2, flexShrink: 0, display: 'flex', gap: 1, overflow: 'auto', pb: 1 }}>
                    {waterSources.map((ws, idx) => (
                      <Box key={ws.id || idx} sx={{ background: 'rgba(255,255,255,0.90)', borderRadius: '12px', border: '1px solid rgba(10,46,31,0.09)', p: 1.5, boxShadow: '0 2px 8px rgba(10,46,31,0.06)', minWidth: 280, transition: 'all 0.2s' }}>
                        <Stack spacing={0.75}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Box sx={{ width: 20, height: 20, borderRadius: '5px', background: 'linear-gradient(135deg, #DBEAFE, #BFDBFE)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <WaterWaveSvg size={11} color="#3B82F6" />
                            </Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F', flex: 1 }}>Source {idx + 1}</Typography>
                            <Button size="small" color="error" disabled={waterSources.length <= 1} onClick={() => setWaterSources((prev) => prev.filter((_, i) => i !== idx))}
                              sx={{ borderRadius: '4px', textTransform: 'none', fontSize: 9, fontFamily: "'DM Sans', sans-serif", py: 0.2, px: 0.75 }}>
                              Remove
                            </Button>
                          </Stack>
                          <TextField label="ID" value={ws.id} onChange={(e) => setWaterSources((prev) => prev.map((p, i) => (i === idx ? { ...p, id: e.target.value } : p)))} size="small" fullWidth sx={{ '& .MuiInputBase-root': { borderRadius: '6px', fontFamily: "'DM Mono', monospace", fontSize: 10 } }} />
                          <TextField label="Name" value={ws.name} onChange={(e) => setWaterSources((prev) => prev.map((p, i) => (i === idx ? { ...p, name: e.target.value } : p)))} size="small" fullWidth sx={{ '& .MuiInputBase-root': { borderRadius: '6px', fontSize: 10 } }} />
                          <TextField label="Type" value={ws.type} onChange={(e) => setWaterSources((prev) => prev.map((p, i) => (i === idx ? { ...p, type: e.target.value } : p)))} size="small" fullWidth sx={{ '& .MuiInputBase-root': { borderRadius: '6px', fontSize: 10 } }} />
                          <Autocomplete
                            multiple
                            size="small"
                            options={plotIdOptions}
                            value={Array.isArray(ws.plotIds) ? ws.plotIds : []}
                            onChange={(_, newVal) => setWaterSources((prev) => prev.map((p, i) => (i === idx ? { ...p, plotIds: newVal } : p)))}
                            renderTags={(val, getTagProps) => val.map((opt, ti) => <Chip key={opt} label={opt} size="small" {...getTagProps({ index: ti })} />)}
                            renderInput={(params) => <TextField {...params} label="Plot IDs" placeholder={plotIdOptions.length ? "Select plots" : "No plots"} sx={{ '& .MuiInputBase-root': { borderRadius: '6px', fontSize: 10 } }} />}
                            sx={{ '& .MuiAutocomplete-tag': { fontSize: 10, height: 20 } }}
                          />
                          <Stack direction="row" spacing={0.5}>
                            <TextField label="Lat" value={ws.lat} onChange={(e) => setWaterSources((prev) => prev.map((p, i) => (i === idx ? { ...p, lat: e.target.value } : p)))} size="small" sx={{ flex: 1, '& .MuiInputBase-root': { borderRadius: '6px', fontFamily: "'DM Mono', monospace", fontSize: 10 } }} />
                            <TextField label="Lng" value={ws.lng} onChange={(e) => setWaterSources((prev) => prev.map((p, i) => (i === idx ? { ...p, lng: e.target.value } : p)))} size="small" sx={{ flex: 1, '& .MuiInputBase-root': { borderRadius: '6px', fontFamily: "'DM Mono', monospace", fontSize: 10 } }} />
                          </Stack>
                          <TextField label="Pump HP" type="number" inputProps={{ step: '0.1', min: '0' }} value={ws.pumpHp} onChange={(e) => setWaterSources((prev) => prev.map((p, i) => (i === idx ? { ...p, pumpHp: e.target.value } : p)))} size="small" fullWidth sx={{ '& .MuiInputBase-root': { borderRadius: '6px', fontSize: 10 } }} />
                        </Stack>
                      </Box>
                    ))}
                    <Box sx={{ minWidth: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Button size="small" variant="outlined" onClick={() => setWaterSources((prev) => [...prev, { id: `ws-${prev.length + 1}`, name: `Water source ${prev.length + 1}`, type: 'Motor', plotIds: [], lat: '', lng: '', pumpHp: '' }])}
                        sx={{ borderRadius: '999px', textTransform: 'none', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 11, borderColor: '#1A7A50', color: '#1A7A50', whiteSpace: 'nowrap' }}>
                        + Add
                      </Button>
                    </Box>
                  </Box>

                  {/* Map Area */}
                  {googleMapsApiKey && mapsLoaded ? (
                    <Box className="map-wrapper" sx={{ flex: 1, overflow: 'hidden', minHeight: 300, mb: 2 }}>
                      <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%', minHeight: 350 }}
                        center={basicMapCenter} zoom={Number.isFinite(Number.parseFloat(latitude)) ? 17 : 5} mapTypeId="hybrid"
                        onClick={(e) => { const lat = e?.latLng?.lat?.(); const lng = e?.latLng?.lng?.(); if (!Number.isFinite(lat) || !Number.isFinite(lng)) return; setWaterSources((prev) => { const emptyIdx = prev.findIndex((w) => !w.lat && !w.lng); if (emptyIdx !== -1) return prev.map((w, i) => (i === emptyIdx ? { ...w, lat: String(lat), lng: String(lng) } : w)); return [...prev, { id: `ws-${prev.length + 1}`, name: `Water source ${prev.length + 1}`, type: 'Motor', plotIds: [], lat: String(lat), lng: String(lng), pumpHp: '' }]; }); }}
                      >
                        {waterSources.map((w, idx) => { const lat = Number.parseFloat(String(w?.lat ?? '').trim()); const lng = Number.parseFloat(String(w?.lng ?? '').trim()); if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null; return <Marker key={`${w.id}-${idx}`} position={{ lat, lng }} draggable onDragEnd={(e) => { const nextLat = e?.latLng?.lat?.(); const nextLng = e?.latLng?.lng?.(); if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return; setWaterSources((prev) => prev.map((p, i) => (i === idx ? { ...p, lat: String(nextLat), lng: String(nextLng) } : p))); }} />; }).filter(Boolean)}
                      </GoogleMap>
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(167,243,208,0.10)', borderRadius: '12px', minHeight: 300, mb: 2 }}>
                      <Typography sx={{ color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif" }}>Map not available.</Typography>
                    </Box>
                  )}

                  {/* Footer Actions */}
                  <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ flexShrink: 0 }}>
                    <Button variant="text" onClick={() => setAddStep(1)} sx={{ textTransform: 'none', fontFamily: "'DM Sans', sans-serif", color: '#6B8A7A' }}>← Back</Button>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" color="inherit" onClick={() => setWaterSources((prev) => (prev.length ? prev.slice(0, -1) : prev))} disabled={submitting || waterSources.length <= 1} sx={{ borderRadius: '6px', textTransform: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: 11, py: 0.5 }}>Undo Last</Button>
                      <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={emeraldBtnSx}>{submitting ? <CircularProgress size={20} color="inherit" /> : 'Save Farm ✓'}</Button>
                    </Stack>
                  </Stack>
                </Box>
              ) : null}

            </>
          )}
        </DialogContent>
        {!showSuccess ? (
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleAddClose} sx={{ textTransform: 'none', fontFamily: "'DM Sans', sans-serif", color: '#6B8A7A' }}>Cancel</Button>
          </DialogActions>
        ) : null}
      </Dialog>

      {/* View farm details dialog */}
      <Dialog open={viewOpen} onClose={() => { setViewOpen(false); setViewDraft(null); setDraftImageUrls([]); setUploadTasks([]); setImageOpsError(''); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px', border: '1px solid rgba(46,204,122,0.20)', boxShadow: '0 24px 80px rgba(10,46,31,0.20)' } }}>
        <DialogTitle sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#0A2E1F', fontSize: 20, pb: 0 }}>
          Farm Details
        </DialogTitle>
        <DialogContent>
          {viewDraft ? (() => {
            const vf = viewDraft?.farm || viewDraft;
            const vLat = Number.parseFloat(String(vf?.latitude ?? ''));
            const vLng = Number.parseFloat(String(vf?.longitude ?? ''));
            const hasCoords = Number.isFinite(vLat) && Number.isFinite(vLng);
            const vPlots = Array.isArray(viewDraft?.plots) ? viewDraft.plots : [];
            const vWaterSources = Array.isArray(viewDraft?.waterSources) ? viewDraft.waterSources : [];
            const vBoundary = Array.isArray(viewDraft?.boundary || vf?.boundary) ? normalizeBoundaryPoints(viewDraft?.boundary || vf?.boundary) : [];

            return (
              <Stack spacing={2} sx={{ mt: 1 }}>
                {/* Farm Profile Card */}
                <Box sx={{ background: 'rgba(255,255,255,0.90)', borderRadius: '16px', border: '1px solid rgba(10,46,31,0.09)', p: 2.5, boxShadow: '0 2px 16px rgba(10,46,31,0.06)' }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'linear-gradient(135deg, #D1FAE5, #A7F3D0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AgricultureIcon sx={{ color: '#0F4D33', fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 18, color: '#0A2E1F' }}>
                          {vf?.farmName || vf?.name || 'Farm'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif" }}>
                          Guest Farm
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider sx={{ borderColor: 'rgba(10,46,31,0.08)' }} />
                    {vf?.phoneNumber ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PhoneIcon sx={{ color: '#1A7A50', fontSize: 16 }} />
                        <Typography sx={{ fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>{vf.phoneNumber}</Typography>
                      </Stack>
                    ) : null}
                    {vf?.locationDescription ? (
                      <Typography sx={{ fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: '#6B8A7A' }}>{vf.locationDescription}</Typography>
                    ) : null}
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      {vf?.size ? (
                        <Box sx={{ background: 'rgba(46,204,122,0.08)', borderRadius: '8px', px: 1.5, py: 0.5 }}>
                          <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#0F4D33' }}>
                            {vf.size} {vf?.sizeUnit || 'acres'}
                          </Typography>
                        </Box>
                      ) : null}
                      {vf?.cropName ? (
                        <Box sx={{ background: 'rgba(46,204,122,0.08)', borderRadius: '8px', px: 1.5, py: 0.5 }}>
                          <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#0F4D33' }}>
                            {vf.cropName}
                          </Typography>
                        </Box>
                      ) : null}
                      {vPlots.length ? (
                        <Box sx={{ background: 'rgba(46,204,122,0.08)', borderRadius: '8px', px: 1.5, py: 0.5 }}>
                          <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#0F4D33' }}>
                            {vPlots.length} plot{vPlots.length > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      ) : null}
                      {vWaterSources.length ? (
                        <Box sx={{ background: 'rgba(59,130,246,0.08)', borderRadius: '8px', px: 1.5, py: 0.5 }}>
                          <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#1E40AF' }}>
                            {vWaterSources.length} water source{vWaterSources.length > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      ) : null}
                    </Stack>
                    {hasCoords ? (
                      <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#6B8A7A' }}>
                        {formatLatLngCompact(vLat, vLng, 5)}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>

                {/* Farm Map */}
                {googleMapsApiKey && mapsLoaded ? (
                  <Box sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(10,46,31,0.09)', height: 380 }}>
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      center={hasCoords ? { lat: vLat, lng: vLng } : defaultMapCenter}
                      zoom={hasCoords ? 16 : 5}
                      mapTypeId="hybrid"
                      options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false, draggable: true, zoomControl: true }}
                    >
                      {hasCoords ? (
                        <Marker
                          position={{ lat: vLat, lng: vLng }}
                          icon={{
                            url: makeSvgMarkerUrl(
                              '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#1A7A50" stroke="#fff" stroke-width="2"/><circle cx="12" cy="9" r="3" fill="#fff"/></svg>'
                            ),
                            scaledSize: typeof window !== 'undefined' && window?.google?.maps ? new window.google.maps.Size(32, 32) : undefined,
                            anchor: typeof window !== 'undefined' && window?.google?.maps ? new window.google.maps.Point(16, 32) : undefined,
                          }}
                        />
                      ) : null}
                      {vBoundary.length >= 3 ? (
                        <Polygon
                          path={vBoundary}
                          options={{ strokeColor: '#1A7A50', strokeOpacity: 0.85, strokeWeight: 3, fillColor: '#1A7A50', fillOpacity: 0.10 }}
                        />
                      ) : null}
                      {vPlots.map((p, pi) => {
                        const pts = normalizeBoundaryPoints(p.boundary || p.boundaryPoints || []);
                        if (pts.length < 3) return null;
                        return (
                          <Polygon
                            key={p.id || pi}
                            path={pts}
                            options={{ strokeColor: '#2ECC7A', strokeOpacity: 0.9, strokeWeight: 2.5, fillColor: '#2ECC7A', fillOpacity: 0.18 }}
                          />
                        );
                      })}
                      {vWaterSources.map((w, wi) => {
                        const wLat = Number.parseFloat(String(w?.location?.lat ?? w?.lat ?? ''));
                        const wLng = Number.parseFloat(String(w?.location?.lng ?? w?.lng ?? ''));
                        if (!Number.isFinite(wLat) || !Number.isFinite(wLng)) return null;
                        return (
                          <Marker
                            key={w.id || wi}
                            position={{ lat: wLat, lng: wLng }}
                            icon={{
                              url: makeSvgMarkerUrl(
                                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M12 2C12 2 6 10 6 15C6 18.3137 8.68629 21 12 21C15.3137 21 18 18.3137 18 15C18 10 12 2 12 2Z" fill="#3B82F6" opacity="0.85"/><circle cx="12" cy="15" r="2" fill="#fff" opacity="0.6"/></svg>'
                              ),
                              scaledSize: typeof window !== 'undefined' && window?.google?.maps ? new window.google.maps.Size(24, 24) : undefined,
                              anchor: typeof window !== 'undefined' && window?.google?.maps ? new window.google.maps.Point(12, 22) : undefined,
                            }}
                            title={`${w.name || 'Water source'}${w.type ? ` (${w.type})` : ''}`}
                          />
                        );
                      })}
                    </GoogleMap>
                  </Box>
                ) : mapsLoadError ? (
                  <Alert severity="error" sx={{ borderRadius: '12px' }}>Maps failed to load.</Alert>
                ) : (
                  <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(167,243,208,0.10)', borderRadius: '12px' }}>
                    <CircularProgress sx={{ color: '#1A7A50' }} />
                  </Box>
                )}

                {/* Plots summary */}
                {vPlots.length ? (
                  <Box sx={{ background: 'rgba(255,255,255,0.90)', borderRadius: '16px', border: '1px solid rgba(10,46,31,0.09)', p: 2, boxShadow: '0 2px 16px rgba(10,46,31,0.06)' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F', mb: 1 }}>Plots ({vPlots.length})</Typography>
                    <Stack spacing={0.75}>
                      {vPlots.map((p, pi) => {
                        const pts = normalizeBoundaryPoints(p.boundary || p.boundaryPoints || []);
                        return (
                          <Stack key={p.id || pi} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 0.5, background: pi % 2 === 0 ? 'rgba(46,204,122,0.04)' : 'transparent', borderRadius: '8px' }}>
                            <Typography sx={{ fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>{p.name || `Plot ${pi + 1}`}</Typography>
                            <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#6B8A7A' }}>{pts.length} pts</Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}

                {/* Water Sources summary */}
                {vWaterSources.length ? (
                  <Box sx={{ background: 'rgba(255,255,255,0.90)', borderRadius: '16px', border: '1px solid rgba(10,46,31,0.09)', p: 2, boxShadow: '0 2px 16px rgba(10,46,31,0.06)' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F', mb: 1 }}>Water Sources ({vWaterSources.length})</Typography>
                    <Stack spacing={0.75}>
                      {vWaterSources.map((w, wi) => {
                        const wLat = w?.location?.lat ?? w?.lat;
                        const wLng = w?.location?.lng ?? w?.lng;
                        return (
                          <Stack key={w.id || wi} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 0.5, background: wi % 2 === 0 ? 'rgba(59,130,246,0.04)' : 'transparent', borderRadius: '8px' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <WaterWaveSvg size={12} color="#3B82F6" />
                              <Typography sx={{ fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>{w.name || `Source ${wi + 1}`}</Typography>
                              {w.type ? <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#6B8A7A' }}>({w.type})</Typography> : null}
                            </Stack>
                            <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: '#6B8A7A' }}>
                              {wLat && wLng ? `${Number(wLat).toFixed(4)}, ${Number(wLng).toFixed(4)}` : '—'}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}

                {/* Images */}
                <Box sx={{ background: 'rgba(255,255,255,0.90)', borderRadius: '16px', border: '1px solid rgba(10,46,31,0.09)', p: 2.25, boxShadow: '0 2px 16px rgba(10,46,31,0.06)' }}>
                  <input ref={filePickerRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => beginUploads(e.target.files)} disabled={imageOpsBusy} />
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ImageOutlinedIcon sx={{ fontSize: 18, color: '#92400E' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: '#1A7A50', letterSpacing: '0.12em' }}>FARM MEDIA</Typography>
                        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: '#0A2E1F' }}>Images</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {imageOpsBusy ? <CircularProgress size={18} sx={{ color: '#1A7A50' }} /> : null}
                      <Button variant="contained" onClick={() => filePickerRef.current?.click()} disabled={imageOpsBusy} sx={{ px: 2.5, ...emeraldBtnSx }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <CloudUploadOutlinedIcon sx={{ fontSize: 18 }} />
                          <span>Upload</span>
                        </Stack>
                      </Button>
                    </Stack>
                  </Stack>
                  {imageOpsError ? <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily: "'DM Sans', sans-serif" }}>{imageOpsError}</Alert> : null}
                  <Box
                    onClick={() => (imageOpsBusy ? null : filePickerRef.current?.click())}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (imageOpsBusy) return; beginUploads(e.dataTransfer?.files); }}
                    sx={{ cursor: imageOpsBusy ? 'not-allowed' : 'pointer', p: 2, borderRadius: '14px', border: '1px dashed rgba(26,122,80,0.35)', background: 'linear-gradient(135deg, rgba(167,243,208,0.12) 0%, rgba(255,255,255,0.92) 100%)', mb: 1.75, textAlign: 'center' }}
                  >
                    <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: 'rgba(26,122,80,0.75)', mb: 0.25 }} />
                    <Typography sx={{ fontWeight: 700, fontFamily: "'DM Sans', sans-serif", color: '#0A2E1F' }}>Click or drag images here</Typography>
                    <Typography sx={{ fontSize: 12, color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif" }}>Upload multiple farm photos.</Typography>
                  </Box>
                  {uploadTasks.length ? (
                    <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                      {uploadTasks.slice(0, 6).map((t) => (
                        <Box key={t.id} sx={{ p: 0.9, borderRadius: '12px', border: '1px solid rgba(10,46,31,0.08)', background: 'rgba(255,255,255,0.85)' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: '#0A2E1F' }}>{t.name}</Typography>
                            <Typography sx={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: t.status === 'error' ? '#EF4444' : '#6B8A7A' }}>
                              {t.status === 'done' ? 'DONE' : t.status === 'error' ? 'FAILED' : `${t.progress}%`}
                            </Typography>
                          </Stack>
                          <LinearProgress variant="determinate" value={Number(t.progress) || 0} sx={{ height: 4, borderRadius: 999, backgroundColor: 'rgba(10,46,31,0.08)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #1A7A50, #2ECC7A)' } }} />
                        </Box>
                      ))}
                    </Stack>
                  ) : null}
                  {draftImageUrls.length === 0 ? (
                    <Box sx={{ p: 2.5, textAlign: 'center', background: 'rgba(167,243,208,0.08)', borderRadius: '14px', border: '1px dashed rgba(46,204,122,0.30)' }}>
                      <Typography sx={{ color: '#6B8A7A', fontFamily: "'DM Sans', sans-serif" }}>No images uploaded yet.</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={1.5}>
                      {draftImageUrls.map((url) => (
                        <Grid item xs={12} sm={6} md={4} key={String(url)}>
                          <Box sx={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(10,46,31,0.10)', boxShadow: '0 2px 14px rgba(10,46,31,0.08)', background: 'white' }}>
                            <Box component="img" src={String(url)} alt="farm" loading="lazy" sx={{ width: '100%', height: 180, objectFit: 'cover', display: 'block', background: 'rgba(10,46,31,0.04)' }} />
                            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.00) 50%, rgba(0,0,0,0.35) 100%)' }} />
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDraftImage(url)}
                              disabled={imageOpsBusy}
                              sx={{ position: 'absolute', right: 8, bottom: 8, background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.35)', color: '#FEE2E2', backdropFilter: 'blur(6px)', '&:hover': { background: 'rgba(239,68,68,0.28)' } }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Stack>
            );
          })() : (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress sx={{ color: '#1A7A50' }} /></Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setViewOpen(false); setViewDraft(null); setDraftImageUrls([]); setUploadTasks([]); setImageOpsError(''); }} sx={{ textTransform: 'none', fontFamily: "'DM Sans', sans-serif", color: '#6B8A7A' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
